import { IconButton, twClassMerge } from '@healthhub/react-ui';
import { Form, Input, Modal } from 'antd';
import { useState } from 'react';

import { NewsLetterImg } from '@shared/assets';
import { FilledCloseIcon } from '@shared/assets-icon';
import { Button } from '@shared/components/Button';
import { CaptchaDisclaimer } from '@shared/components/captcha';
import { PortalCheckbox } from '@shared/components/V2';
import { sitecoreHomeBaseUrl } from '@shared/configs/configs/config';
import { SITE_CORE_URL } from '@shared/constants/paths';
import useInvisibleCaptcha from '@shared/hooks/useInvisibleCaptcha';
import { useLazyGetReCaptchaQuery } from '@shared/services/validateCaptcha';
import { emailRule } from '@shared/utils/validator';

import { handleNewsLetterSubscriptionWithNewTab } from './handleNewsLetterSubscriptionWithNewTab';
import styles from './styles.module.css';

const { useForm } = Form;

interface FormValues {
  acceptTou: boolean;
  email: string;
}

interface NewsLetterModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const NewsLetterModal = ({ isOpen, onClose }: NewsLetterModalProps) => {
  const [form] = useForm<FormValues>();
  const [getReCaptchaData, { isLoading }] = useLazyGetReCaptchaQuery();
  const { getToken, turnstileWidget, captchaProvider } = useInvisibleCaptcha();
  const defaultValidationMode: { [key in keyof FormValues]: 'onBlur' | 'onChange' } = {
    acceptTou: 'onChange',
    email: 'onBlur',
  };
  const [validationMode, setValidationMode] = useState(defaultValidationMode);
  const onSubmit = async (values: FormValues) => {
    await handleNewsLetterSubscriptionWithNewTab({
      getToken,
      onValidate: async (token) => {
        const data = await getReCaptchaData({ ResponseToken: token, Version: 'v3' }).unwrap();
        return !!data;
      },
      email: values.email,
      onError: (error) => console.error('Captcha execution failed:', error),
      onFinally: () => form.resetFields(),
    });
  };

  const changeValidationMode = (fieldName: keyof FormValues) => {
    setValidationMode((prevState) => ({ ...prevState, [fieldName]: 'onChange' }));
  };

  const onCloseModal = () => {
    onClose();
    form.resetFields();
    setValidationMode(defaultValidationMode);
  };

  return (
    <Modal
      centered
      className={styles.modal}
      closable={false}
      destroyOnHidden
      footer={null}
      onCancel={onCloseModal}
      open={isOpen}
      width={520}
    >
      <Form className="relative flex flex-col items-center gap-8" form={form} onFinish={onSubmit}>
        <IconButton
          aria-label="Close"
          className="absolute -right-4 -top-5"
          icon={<FilledCloseIcon className="size-6" />}
          onClick={onCloseModal}
        />
        <div className="flex flex-col items-center gap-4">
          <img alt="" className="h-auto w-96" src={NewsLetterImg} />
          <h4 className="m-0 text-2xl font-semibold">Stay in the know</h4>
        </div>
        <p className="m-0 text-center text-base">
          Join our newsletter mailing list to receive relevant health Information, tips and more
        </p>
        <div className="flex w-full flex-col gap-4 lg:flex-row">
          <Form.Item<FormValues>
            className="m-0 flex-1 [&_.ant-form-item-explain-error]:w-full"
            name="email"
            rules={[
              { message: 'Please key in your email address.', required: true },
              emailRule('Email contains an invalid email address.'),
            ]}
            validateTrigger={validationMode.email}
          >
            <Input
              autoComplete="email"
              className={twClassMerge('h-10 w-full rounded-lg', styles.input)}
              onBlur={() => changeValidationMode('email')}
              placeholder="Email your email address"
            />
          </Form.Item>
          <Button className="json-checked h-10 bg-primary-blue-grey-3 py-0" htmlType="submit" loading={isLoading}>
            Sign up now
          </Button>
        </div>
        <Form.Item<FormValues>
          className="m-0 [&_.ant-form-item-explain-error]:w-full"
          name="acceptTou"
          rules={[
            {
              validator: (_, value) =>
                value
                  ? Promise.resolve()
                  : Promise.reject(new Error('Please confirm you have read the Privacy Policy.')),
            },
          ]}
          valuePropName="checked"
        >
          <PortalCheckbox className={twClassMerge('text-xs', styles.checkBox)}>
            By subscribing to our e-Newsletter, you have accepted HealthHub’s{' '}
            <a
              className="text-primary-green-4 underline"
              href={`${sitecoreHomeBaseUrl()}${SITE_CORE_URL.termsOfUse}`}
              rel="noopener noreferrer"
              target="_blank"
            >
              Terms of Use
            </a>{' '}
            and acknowledged that you read and understood our{' '}
            <a
              className="text-primary-green-4 underline"
              href={`${sitecoreHomeBaseUrl()}${SITE_CORE_URL.privacyPolicy}`}
              rel="noopener noreferrer"
              target="_blank"
            >
              Privacy Policy.
            </a>
          </PortalCheckbox>
        </Form.Item>
        <CaptchaDisclaimer captchaProvider={captchaProvider} className="p-0 text-xs text-functional-iron" />
      </Form>
      {turnstileWidget}
    </Modal>
  );
};

export default NewsLetterModal;
